import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { getCaseConfigurations, createUpdateCaseConfigurations, getCaseConfigurationsMetadata } from "requests/caseConfigurations";
import { MCard, MSpinner, MSelect, MDialog, ACTION_APPLY, ACTION_CLOSE } from "components/Shared";
import { TextField, Button, Grid, Cell } from "react-md";
import { CaseConfigurationsProperties, CaseConfigurationsMetadata, SurveyTypeListDto, CustomerDetailsDto } from "interfaces/Mueller.Business.Models";

interface CaseConfigsRow {
  key: string;
  name: string;
  desc: string;
  inheritedValue?: string;
  inheritSource?: string;
  isValueSetHere: boolean;
}

interface LabelValue {
  label: string;
  value: string;
}

enum EditMode {
  SPECIFIC = "SPECIFIC",
  CUSTOMER = "CUSTOMER",
}

interface Props {
  // redux
  customerDetails: CustomerDetailsDto;
  fetchingCustomerDetails: boolean;
}

const CaseConfigurations = ({
  customerDetails,
  fetchingCustomerDetails,
}: Props) => {
  const mounted = useRef<boolean>(true);

  const [configsMetadata, setConfigsMetadata] = useState<CaseConfigurationsMetadata[] | null>(null);
  const [customerSurveyTypeOptions, setCustomerSurveyTypeOptions] = useState<LabelValue[] | null>(null);
  
  // UI state management
  const [isUpdateRequestRunning, setIsUpdateRequestRunning] = useState<boolean>(false);
  const [editingMetadata, setEditingMetadata] = useState<CaseConfigurationsMetadata | null>(null);
  const [editingConfiguration, setEditingConfiguration] = useState<CaseConfigurationsProperties | null>(null);
  const editingRef = useRef<string | null>(null);

  // drop down selections
  const [selectedSurveyType, setSelectedSurveyType] = useState<string | null>(null);

  // for configurations: undefined means no data from webservice yet, null means no settings/overrides for the selections
  const [defaultConfigurations, setDefaultConfigurations] = useState<CaseConfigurationsProperties | null | undefined>(undefined);
  const [surveyTypeConfigurations, setSurveyTypeConfigurations] = useState<CaseConfigurationsProperties | null | undefined>(undefined);
  const [customerConfigurations, setCustomerConfigurations] = useState<CaseConfigurationsProperties | null | undefined>(undefined);
  const [specificConfigurations, setSpecificConfigurations] = useState<CaseConfigurationsProperties | null | undefined>(undefined);

  useEffect(() => {
    getCaseConfigurationsMetadata((meta) => {
      setConfigsMetadata(meta);
    }, mounted);
    getCaseConfigurations(null, null, (configs) => {
      setDefaultConfigurations(configs);
    }, mounted);
  }, []);

  useEffect(() => {
    if (customerDetails && !fetchingCustomerDetails) {
      setCustomerSurveyTypeOptions(customerDetails.configuredSurveyTypes.map((o) => ({ label: `${o.name}${o.inactiveDate ? " (INACTIVE)" :""}`, value: o.code })));
              
      getCaseConfigurations(null, customerDetails.number.toString(), (configs) => {
        setCustomerConfigurations(configs);
      }, mounted);
    }
    else {
      setSelectedSurveyType(null);
      setCustomerSurveyTypeOptions(null);
      setCustomerConfigurations(undefined);
      setSurveyTypeConfigurations(undefined);
      setSpecificConfigurations(undefined);
    }
  }, [customerDetails]);
  
  if (configsMetadata === null || defaultConfigurations === undefined || customerConfigurations === undefined) return <MSpinner />

  let currentMode = selectedSurveyType !== null ? EditMode.SPECIFIC : EditMode.CUSTOMER;

  const surveyTypeOnChange = (v: { label: string, value: string}) => {
    if (v) {
      setSelectedSurveyType(v.value);

      getCaseConfigurations(v.value, null, (configs) => {
        setSurveyTypeConfigurations(configs);
      }, mounted);
      getCaseConfigurations(v.value, customerDetails.number.toString(), (configs) => {
        setSpecificConfigurations(configs);
      }, mounted);
    }
    else {
      setSelectedSurveyType(null);
      setSurveyTypeConfigurations(null);
      setSpecificConfigurations(null);
    }
  };

  const editField = (key: string) => {
    const meta: CaseConfigurationsMetadata = configsMetadata.filter(m => m.key === key)[0];
    let config = null;
    if (currentMode === EditMode.SPECIFIC) {
      config = specificConfigurations;
    }
    else if (currentMode === EditMode.CUSTOMER) {
      config = customerConfigurations;
    }

    if (config === null) { // creating new config for selection
      config = {};
    }

    editingRef.current = null;
    setEditingMetadata(meta);
    setEditingConfiguration(config);
  };

  const deleteField = (key: string) => {
    let config = null;
    if (currentMode === EditMode.SPECIFIC) {
      config = specificConfigurations;
    }
    else if (currentMode === EditMode.CUSTOMER) {
      config = customerConfigurations;
    }

    if (config !== null) {
      config[key] = null;
      setIsUpdateRequestRunning(true);
      createUpdateCaseConfigurations(selectedSurveyType, customerDetails.number.toString(), config, () => {
        refreshAfterUpdate();
      }, mounted);
    }
  };

  const refreshAfterUpdate = () => {
    let setter = null;
    if (currentMode === EditMode.SPECIFIC) {
      setter = setSpecificConfigurations;
    }
    else if (currentMode === EditMode.CUSTOMER) {
      setter = setCustomerConfigurations;
    }

    if (setter !== null) {
      getCaseConfigurations(selectedSurveyType, customerDetails.number.toString(), (configs) => {
        setter(configs);
        setIsUpdateRequestRunning(false);
      }, mounted);
    }
  };

  const buildRowData = (): CaseConfigsRow[] => {
    const rows:CaseConfigsRow[] = [];

    for (const meta of configsMetadata) {
      const row: CaseConfigsRow = {
        key: meta.key,
        name: meta.name,
        desc: meta.description,
        inheritSource: null,
        inheritedValue: null,
        isValueSetHere: false,
      };

      // coalesce using same logic from stored procedure to determine effective values
      if (specificConfigurations && specificConfigurations[meta.key] !== null) {
        row.inheritedValue = specificConfigurations[meta.key].toString();
        row.inheritSource = "(From: Customer+SurveyType)";

        row.isValueSetHere = currentMode === EditMode.SPECIFIC;
      }
      else if (customerConfigurations && customerConfigurations[meta.key] !== null) {
        row.inheritedValue = customerConfigurations[meta.key].toString();
        row.inheritSource = "(From: Customer Default)";

        row.isValueSetHere = currentMode === EditMode.CUSTOMER;
      }
      else if (surveyTypeConfigurations && surveyTypeConfigurations[meta.key] !== null) {
        row.inheritedValue = surveyTypeConfigurations[meta.key].toString();
        row.inheritSource = "(From: Survey Type Default)";
      }
      else if (defaultConfigurations[meta.key] !== null) {
        row.inheritedValue = defaultConfigurations[meta.key].toString();
        row.inheritSource = "(From: Mueller Default)";
      }
      else {
        row.inheritSource = "(No Setting or Default)";
      }

      rows.push(row);
    }

    return rows;
  };

  const onEditDialogClose = () => {
    setEditingConfiguration(null);
    setEditingMetadata(null);
  };

  const onEditDialogApply = () => {
    if (editingRef.current === null) {
      onEditDialogClose();
      return;
    }

    let submitValue: any = null;
    switch (editingMetadata.controlType) {
      case "Text":
        submitValue = editingRef.current.trim();
        break;
      case "Decimal":
        const parsedD = parseFloat(editingRef.current);
        if (!isNaN(parsedD))
          submitValue = parsedD;
        break;
      case "Integer":
        const parsedI = parseInt(editingRef.current);
        if (!isNaN(parsedI))
          submitValue = parsedI;
        break;
      case "Checkbox":
        submitValue = editingRef.current === "true";
        break;
      default:
        submitValue = editingRef.current;
        break;
    }

    editingConfiguration[editingMetadata.key] = submitValue;
    setEditingMetadata(null);
    setIsUpdateRequestRunning(true);

    createUpdateCaseConfigurations(selectedSurveyType, customerDetails.number.toString(), editingConfiguration, () => {
      setEditingConfiguration(null);
      setIsUpdateRequestRunning(false);
      refreshAfterUpdate();
    }, mounted);
  };

  const getDialogsSection = (): React.ReactNode => {
    if (!editingConfiguration || !editingMetadata) return (<div />);

    let initialValue = "";
    if (editingConfiguration[editingMetadata.key])
      initialValue = editingConfiguration[editingMetadata.key].toString();

    let dialogControl: React.ReactNode = null;
    switch (editingMetadata.controlType) {
      case "Text":
      case "TextArea":
      case "JSON":
        let rows = undefined;
        if (editingMetadata.controlType === "TextArea") rows = 3;
        else if (editingMetadata.controlType === "JSON") rows = 10;
        dialogControl = (
          <TextField
            id={`text-${editingMetadata.key}`}
            defaultValue={initialValue}
            placeholder={"Enter value here..."}
            type={"text"}
            rows={rows}
            onChange={v => { editingRef.current = v.toString() }}
          />
        );
        break;
      case "Decimal":
      case "Integer":
        dialogControl = (
          <TextField
            id={`text-${editingMetadata.key}`}
            defaultValue={initialValue}
            placeholder={"Enter value here..."}
            type={"number"}
            onChange={v => { editingRef.current = v.toString() }}
          />
        );
        break;
      case "Select":
        dialogControl = (
          <MSelect
            id={`select-${editingMetadata.key}`}
            defaultValue={initialValue}
            options={editingMetadata.options.map(o => ({ label: o, value: o}))}
            onChange={v => { editingRef.current = v.value }}
          />
        );
        break;
      case "Checkbox":
        dialogControl = (
          <MSelect
            id={`select-${editingMetadata.key}`}
            defaultValue={initialValue}
            options={[
              {label: "false", value: "false"},
              {label: "true", value: "true"},
            ]}
            onChange={v => { editingRef.current = v.value }}
          />
        );
        break;
    }

    return (
      <MDialog
        id="edit-caseconfigurations-field"
        title="Edit"
        onHide={onEditDialogClose}
        actions={[
          { code: ACTION_CLOSE, onClick: onEditDialogClose },
          { code: ACTION_APPLY, onClick: onEditDialogApply },
        ]}
      >
        {dialogControl}
      </MDialog>
    );
  };

  
  let editSection: React.ReactNode;
  if (customerConfigurations === undefined) { // still waiting for customer configs to load
    editSection = (<div />);
  }
  else if (selectedSurveyType !== null && surveyTypeConfigurations === undefined && specificConfigurations === undefined) { // selected a survey type, waiting for configs
    editSection = (<div />);
  }
  else {
    editSection = (
      <div className="case-configurations-edit-container">
        <Grid>
          <Cell size={4}><h3>Name</h3></Cell>
          <Cell size={4}><h3>Value</h3></Cell>
          <Cell size={4}></Cell>
        </Grid>
        {buildRowData().map(row => (
            <Grid key={row.key}>
              <Cell size={4}><h5>{row.name}</h5><h6 style={{fontSize:"0.8em"}}>{row.desc}</h6></Cell>
              <Cell size={4}><h5 className="case-config-inherited-value">{row.inheritedValue}</h5><h6 style={{color:"green"}}>{row.inheritSource}</h6></Cell>
              <Cell size={4}>
                {row.isValueSetHere &&
                <>
                <Button 
                  raised 
                  primary 
                  disabled={isUpdateRequestRunning}
                  onClick={() => editField(row.key)}
                  >Edit</Button>
                <Button 
                  raised 
                  secondary 
                  disabled={isUpdateRequestRunning}
                  onClick={() => deleteField(row.key)}
                  >Delete</Button>
                </>
                }
                {!row.isValueSetHere &&
                <Button 
                  raised 
                  disabled={isUpdateRequestRunning}
                  onClick={() => editField(row.key)}
                  >Set</Button>
                }
              </Cell>
            </Grid>
          )
        )}
      </div>
    );
  }

  return (
    <>
      {getDialogsSection()}
      { (customerDetails && customerConfigurations !== undefined && customerSurveyTypeOptions?.length) &&
      <>
        <MCard 
          title="Account Details"
          isEditable={false}
          fields={[
            { label: "Account Number", value: customerDetails.number.toString()},
            { label: "Account Name", value: customerDetails.name},
          ]}
        />
        <div style={{marginTop:"15px", marginBottom:"5px"}}>
          <MSelect
            id="caseconfigurations-survey-type"
            onChange={surveyTypeOnChange}
            options={customerSurveyTypeOptions}
            isClearable
            placeholder="Select Survey Type - Viewing Customer Defaults"
          />
        </div>
      </>
      }
      {editSection}
    </>
  );
};

const mapStateToProps = state => ({
  customerDetails: state.customer.customerDetails,
  fetchingCustomerDetails: state.customer.fetchingCustomerDetails,
});

export default connect(mapStateToProps)(CaseConfigurations);